// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'

// Internal Dependencies:
import NowPlayingButton from 'components/NowPlaying/NowPlayingButton'
import TrackBar from './TrackBar'
import TimeShift from 'components/NowPlaying/TimeShift'
import SiteNotice from 'components/SiteNotice/SiteNotice'

// Redux:
import { goToPreviousTrack, goToNextTrack, rewind, fastForward, setSeeking, calculateSeekPercent } from 'actions/schedule'
import { likeProgram, showProgramMenu } from 'actions/site'

class NowPlayingMobileApp extends React.Component {
	constructor( props ) {
		super( props )

		this.state = {
			clickedLike: false,
			clickedDislike: false,
			showLikeNotice: false,
			showFirstLikeNotice: false
		}
	}

	handlePreviousClick = () => {
		const { goToPreviousTrack } = this.props

		goToPreviousTrack()
	}

	handleNextClick = () => {
		const { goToNextTrack, canGoToNext } = this.props

		if ( canGoToNext ) {
			goToNextTrack()
		}
	}

	handleRewindClick = () => {
		this.props.rewind()
	}

	handleFastForwardClick = () => {
		const { fastForward, setSeeking, calculateSeekPercent, canFastForward } = this.props

		if ( canFastForward ) {
			setSeeking( true )

			setTimeout( () => {
				fastForward()
			}, 100 )

			setTimeout( () => {
				setSeeking( false )
				calculateSeekPercent()
			}, 3000 )
		}
	}

	handleLiveClick = () => {
		this.props.goToLive()
	}

	handleLikeClick = ( e ) => {
		e.preventDefault()
		e.stopPropagation()

		const { loggedIn, likeProgram, nowPlaying, accountData } = this.props

		if ( ! loggedIn ) {
			this.setState( {
				showLikeNotice: true
			} )

			return
		}

		if ( ! nowPlaying || ! nowPlaying.audio_file_url ) {
			return
		}

		if ( accountData && ! accountData.likes || ! Object.keys( accountData.likes ).length ) {
			this.setState( {
				showFirstLikeNotice: true
			} )
		}

		likeProgram( nowPlaying )

		this.setState( {
			clickedLike: true
		} )

		setTimeout( () => {
			this.setState( {
				clickedLike: false
			} )
		}, 700 )
	}

	handleCloseLikeNotice = () => {
		this.setState( {
			showLikeNotice: false,
			showFirstLikeNotice: false
		} )
	}

	handleOpenProgramMenu = () => {
		const { showProgramMenu, nowPlaying } = this.props

		if ( ! nowPlaying ) {
			return
		}

		showProgramMenu( nowPlaying )
	}

	render() {
		const { isBuffering, isLive, nowPlaying, isPlaying, mediaError, canFastForward, canGoToNext, showRewindWarning, rewindMaxHours, regularMobile, programsById, accountData } = this.props
		const { clickedLike, clickedDislike, showLikeNotice, showFirstLikeNotice } = this.state
		let wrapClassName = 'now-playing-wrap'
		let rewindClass = '-rewind'
		let fastForwardClass = canFastForward ? '-fast-forward' : '-fast-forward is-disabled'
		const nextClass = canGoToNext ? '-next icon-angle-double-right-light' : '-next icon-angle-double-right-light is-disabled'
		let isLiveStream = nowPlaying.is_livestream
		let playStateText = 'Now Playing:'
		let imageUrl = false

		if ( isLiveStream && isLive ) {
			rewindClass += ' is-disabled'
			fastForwardClass += ' is-disabled'
		}

		let showLoading = false

		if ( isBuffering && isPlaying && ! showRewindWarning ) {
			showLoading = true
			wrapClassName += ' is-loading'

			if ( isLiveStream ) {
				playStateText = 'Streaming:'
			} else {
				playStateText = 'Loading:'
			}
		}

		if ( isPlaying && ! showLoading && ! mediaError ) {
			wrapClassName += ' is-playing'
		}

		if ( mediaError ) {
			if ( isLiveStream ) {
				playStateText = 'Livestream currently unavailable.'
			} else {
				playStateText = 'Failed to load:'
			}
		}

		if ( nowPlaying && nowPlaying.is_program && programsById && programsById[ `id_${nowPlaying.series_slug}` ] ) {
			imageUrl = programsById[ `id_${nowPlaying.series_slug}` ].featured_image_index.url
		}
		let imageClass = imageUrl ? '-image' : '-image is-default'

		let showGuests = nowPlaying.guests && nowPlaying.guests.length > 0 ? true : false
		const dateTime = DateTime.local()

		let likeKey = ''
		let isLiked = false
		let isDisliked = false
		let likeButtonsClass = '-like-buttons is-active'
		let likeButtonClass = '-like'

		if ( nowPlaying && nowPlaying.audio_file_url ) {
			likeKey = nowPlaying.audio_file_url
		}

		if ( clickedLike ) {
			isLiked = true
			likeButtonClass += ' is-clicked'
		}

		if ( accountData && accountData.likes && accountData.likes[ likeKey ] ) {
			isLiked = true
			likeButtonClass += ' is-active'
		}

		return (
			<div className={ wrapClassName }>
				{ showLikeNotice &&
					<SiteNotice
						text="Please log in or create an account to use this feature"
						cancelText="Cancel"
						handleCancel={ this.handleCloseLikeNotice }
					>
						<div className="-actions">
							<a href="/signin">Log in</a>
						</div>
					</SiteNotice>
				}

				{ showFirstLikeNotice &&
					<SiteNotice
						text="This program has been added to your favourites"
						cancelText="OK"
						handleCancel={ this.handleCloseLikeNotice }
					>
						<div className="-actions">
							<Link to="/favourites" onClick={ this.handleCloseLikeNotice }>View my favourites</Link>
						</div>
					</SiteNotice>
				}

				<div className="now-playing">
					<div className="-inner">
						<div className={ imageClass }>
							<div className="-image-inner">
								<div onClick={ this.handleOpenProgramMenu }>
									{ imageUrl &&
										<img src={ imageUrl } alt={ nowPlaying.series } />
									||
										<img src={ `${siteData.images_url}programs/program-default-thumbnail-r4.png` } alt="" />
									}
								</div>
							</div>
						</div>

						{ showRewindWarning &&
							<div className="-note">
								You may only rewind up to { rewindMaxHours } hours.<br />
								Greater access coming soon.
							</div>
						}

						{ ! showRewindWarning && nowPlaying &&
							<div className="-text" onClick={ this.handleOpenProgramMenu }>
								<h4>{ playStateText }</h4>

								{ nowPlaying.is_program &&
									<h3>
										<span className="-series" dangerouslySetInnerHTML={ { __html: nowPlaying.series } } />
										<br />
										<span className="-title" dangerouslySetInnerHTML={ { __html: nowPlaying.title } } />
									</h3>
								||
									<h3>Ads and promos</h3>
								}

								{ nowPlaying.is_program && nowPlaying.guests && nowPlaying.guests.length > 0 &&
									<div className="-guests">
										Guests: <span dangerouslySetInnerHTML={ { __html: nowPlaying.guests } } />
									</div>
								}

								{ ! regularMobile &&
									<TimeShift />
								}

								<div className={ likeButtonsClass }>
									<div className={ likeButtonClass }>
										{ isLiked &&
											<i className="icon-heart" onClick={ this.handleLikeClick } />
										||
											<i className="icon-heart-o" onClick={ this.handleLikeClick } />
										}
									</div>
								</div>
							</div>
						}

						<div className="-controls">
							<i className="-previous icon-angle-double-left-light" aria-hidden="true" onClick={ this.handlePreviousClick }></i>

							<div className={ rewindClass }>
								<i className="icon-undo-regular" aria-hidden="true" onClick={ this.handleRewindClick }></i>

								<span className="-thirty">30</span>
							</div>

							{ regularMobile &&
								<NowPlayingButton />
							}

							<div className={ fastForwardClass }>
								<i className="icon-redo-regular" aria-hidden="true" onClick={ this.handleFastForwardClick }></i>

								<span className="-thirty">30</span>
							</div>

							<i className={ nextClass } aria-hidden="true" onClick={ this.handleNextClick }></i>
						</div>

						<TrackBar />

						{ regularMobile &&
							<TimeShift />
						}
					</div>
				</div>
			</div>
		)
	}
}


const mapStateToProps = ( state, ownProps ) => {
	return {
		isBuffering: state.schedule.isBuffering,
		isPlaying: state.schedule.isPlaying,
		isLive: state.schedule.isLive,
		mediaError: state.schedule.mediaError,
		canFastForward: state.schedule.canFastForward,
		canGoToNext: state.schedule.canGoToNext,
		showRewindWarning: state.schedule.showRewindWarning,
		nowPlaying: state.schedule.nowPlaying,
		nowPlayingLive: state.schedule.nowPlayingLive,
		siteId: state.schedule.siteId,
		rewindMaxHours: state.schedule.rewindMaxHours,
		programsById: state.programs.byId,
		accountData: state.site.accountData,
		loggedIn: state.site.loggedIn
	}
}

const mapDispatchToProps = ( dispatch, ownProps ) => {
	return {
		goToPreviousTrack() {
			dispatch( goToPreviousTrack() )
		},

		goToNextTrack() {
			dispatch( goToNextTrack() )
		},

		rewind() {
			dispatch( rewind() )
		},

		fastForward() {
			dispatch( fastForward() )
		},

		calculateSeekPercent() {
			dispatch( calculateSeekPercent() )
		},

		setSeeking( value ) {
			dispatch( setSeeking( value ) )
		},

		likeProgram( key ) {
			dispatch( likeProgram( key ) )
		},

		showProgramMenu( program ) {
			dispatch( showProgramMenu( program, 'now-playing' ) )
		}
	}
}

export default connect( mapStateToProps, mapDispatchToProps )( NowPlayingMobileApp )
